import { createClient } from '@supabase/supabase-js';

/**
 * CRM v2 Project Token 조회 유틸리티
 *
 * Supabase에서 workspace의 project_token을 런타임에 조회합니다.
 * 토큰은 앱 생명주기 동안 캐싱되어 매 요청마다 Supabase를 조회하지 않습니다.
 */

let cachedToken: string | null = null;
let tokenFetchAttempted = false;

/**
 * CRM v2 Project Token을 조회합니다.
 * - 첫 호출 시 Supabase에서 조회 후 캐싱
 * - 이후 호출은 캐시된 값 반환
 * - 조회 실패 시 null 반환 (해당 세션에서 CRM v2 비활성화)
 */
export async function getCrmV2ProjectToken(): Promise<string | null> {
  // 이미 캐시된 토큰이 있으면 반환
  if (cachedToken) {
    return cachedToken;
  }

  // 🎯 환경변수에서 직접 제공된 토큰 우선 사용 (Supabase DB 쿼리 불필요)
  const envToken = process.env.NEXT_PUBLIC_CRM_V2_PROJECT_TOKEN;
  if (envToken) {
    cachedToken = envToken;
    tokenFetchAttempted = true;
    return cachedToken;
  }

  // 이미 조회 시도했으면 (실패했어도) 다시 시도하지 않음
  if (tokenFetchAttempted) {
    return null;
  }

  tokenFetchAttempted = true;

  // Fallback: 환경변수 미설정 시 Supabase에서 조회
  const supabaseUrl = process.env.NEXT_PUBLIC_CRM_V2_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_CRM_V2_SUPABASE_ANON_KEY;
  const workspaceId = process.env.NEXT_PUBLIC_CRM_V2_WORKSPACE_ID;

  // 환경 변수 검증
  if (!supabaseUrl || !supabaseAnonKey || !workspaceId) {
    return null;
  }

  try {
    const supabase = createClient(supabaseUrl, supabaseAnonKey);

    const { data, error } = await supabase
      .schema('crm')
      .from('workspaces')
      .select('project_token')
      .eq('id', workspaceId)
      .maybeSingle();

    if (error || !data?.project_token) {
      return null;
    }

    cachedToken = data.project_token;
    return cachedToken;
  } catch {
    return null;
  }
}

/**
 * CRM v2 설정이 유효한지 확인합니다.
 */
export function isCrmV2Configured(): boolean {
  return !!(
    process.env.NEXT_PUBLIC_CRM_V2_SUPABASE_URL &&
    process.env.NEXT_PUBLIC_CRM_V2_SUPABASE_ANON_KEY &&
    process.env.NEXT_PUBLIC_CRM_V2_WORKSPACE_ID
  );
}

/**
 * CRM v2 Supabase URL을 반환합니다.
 */
export function getCrmV2SupabaseUrl(): string | undefined {
  return process.env.NEXT_PUBLIC_CRM_V2_SUPABASE_URL;
}
